import React, { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import { useProviderCredentials } from "@/hooks/useProviderCredentials";
import type {
  CloudProviderConfig,
  ComingSoonProvider,
} from "@/types/onboarding";
import ProviderSelection from "@/components/CloudProvider/ProviderSelection";
import ProviderSetupForm from "@/components/CloudProvider/ProviderSetupForm";
import { ProviderCredentialRequest } from "@/types/providercredentails";

// Constants
const PROVIDER_CONFIGS: Record<string, CloudProviderConfig> = {
  gcp: {
    name: "Google Cloud Platform",
    shortName: "GCP",
    logo: "/cloud1.svg",
    fields: [
      {
        name: "projectId",
        label: "Project ID",
        type: "input",
        placeholder: "your-project-id",
      },
      {
        name: "workloadToken",
        label: "Workload Identity Token",
        type: "textarea",
        placeholder: "Paste your Workload Identity Token here",
        hasInfo: true,
      },
      {
        name: "displayName",
        label: "Display Name (Optional)",
        type: "input",
        placeholder: "My GCP Project",
      },
      {
        name: "datasetId",
        label: "Dataset ID",
        type: "input",
        placeholder: "your-bigquery-dataset-id",
      },
      {
        name: "tableId",
        label: "Table ID",
        type: "input",
        placeholder: "your-bigquery-table-id",
      },
    ],
  },
  azure: {
    name: "Microsoft Azure",
    shortName: "Azure",
    logo: "/cloud3.svg",
    fields: [
      {
        name: "subscriptionId",
        label: "Subscription ID",
        type: "input",
        placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
      },
      {
        name: "bearerToken",
        label: "Azure Bearer Token",
        type: "textarea",
        placeholder: "Paste your Azure Bearer Token here",
        hasInfo: true,
      },
      {
        name: "displayName",
        label: "Display Name (Optional)",
        type: "input",
        placeholder: "My Azure Subscription",
      },
    ],
  },
};

const COMING_SOON_PROVIDERS: ComingSoonProvider[] = [
  { id: "aws", name: "Amazon Web Services", logo: "/cloud2.svg" },
];

// Main component
const CloudProviderSetup: React.FC = () => {
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});

  // use unified hook
  const {
    data: credentials,
    isLoading: loadingCredentials,
    isError: errorCredentials,
    testConnection,
    setupCredential,
  } = useProviderCredentials();

  const handleConnect = (providerId: string) => {
    setSelectedProvider(providerId);
    setFormData({});
    // clear any previous test state
    testConnection.reset();
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value, __saveError: undefined }));
    // Clear test result when form data changes
    if (testConnection.isSuccess || testConnection.isError) {
      testConnection.reset();
    }
  };

  const buildAzureRequest = (): ProviderCredentialRequest => ({
    provider: "azure",
    account_identifier: formData.subscriptionId,
    account_type: "subscription",
    display_name:
      formData.displayName || `Azure Subscription - ${formData.subscriptionId}`,
    provider_config: {
      access_token: formData.bearerToken,
    },
    status: "pending",
  });

  const buildGCPRequest = (): ProviderCredentialRequest => {
    return {
      provider: "gcp",
      account_identifier: formData.projectId,
      account_type: "project",
      display_name:
        formData.displayName || `GCP Project - ${formData.projectId}`,
      provider_config: {
        access_token: formData.workloadToken,
        workload_identity_pool: formData.workloadIdentityPool,
        workload_identity_provider: formData.workloadIdentityProvider,
        service_account_email: formData.serviceAccountEmail,
        dataset_id: formData.datasetId,
        table_id: formData.tableId,
      },
      status: "pending",
    };
  };

  const handleTestConnection = () => {
    // Clear any previous save errors
    setFormData((prev) => ({ ...prev, __saveError: undefined }));
    if (selectedProvider === "azure") {
      testConnection.mutate(buildAzureRequest());
    } else {
      testConnection.mutate(buildGCPRequest());
    }
  };

  const handleSaveAndContinue = () => {
    const request =
      selectedProvider === "azure" ? buildAzureRequest() : buildGCPRequest();
    setupCredential.mutate(request, {
      onSuccess: () => navigate("/", { replace: true }),
      onError: (err: any) => {
        const msg =
          err?.response?.data?.detail ||
          (err as Error)?.message ||
          "Failed to save credentials";
        setFormData((prev) => ({ ...prev, __saveError: msg }));
      },
    });
  };

  // Show setup form if provider is selected
  if (selectedProvider && PROVIDER_CONFIGS[selectedProvider]) {
    const config = PROVIDER_CONFIGS[selectedProvider];

    // derive testResult from mutation
    const testResult = testConnection.isSuccess
      ? { success: true, message: "Connection successful!" }
      : testConnection.isError
      ? {
          success: false,
          message:
            (testConnection.error as any)?.response?.data?.detail ||
            (testConnection.error as Error)?.message ||
            "Connection test failed",
          isDetailedError: !!(testConnection.error as any)?.response?.data
            ?.detail,
        }
      : null;

    return (
      <ScrollArea className="h-full">
        <div className="flex items-center justify-center h-full p-6">
          <ProviderSetupForm
            config={config}
            formData={formData}
            isLoading={testConnection.isPending}
            isSaving={setupCredential.isPending}
            testResult={testResult}
            saveErrorMessage={(formData as any).__saveError || null}
            onInputChange={handleInputChange}
            onTestConnection={handleTestConnection}
            onSaveAndContinue={handleSaveAndContinue}
          />
        </div>
      </ScrollArea>
    );
  }
  // Show provider selection
  return (
    <ScrollArea className="h-full p-6">
      <ProviderSelection
        configs={PROVIDER_CONFIGS}
        comingSoon={COMING_SOON_PROVIDERS}
        showMore={showMore}
        onConnect={handleConnect}
        toggleShowMore={() => setShowMore((prev) => !prev)}
      />
    </ScrollArea>
  );
};

export default CloudProviderSetup;
